import { useNavigate } from 'react-router-dom'
import { FolderPlus, GripVertical, Trash2 } from 'lucide-react'
import { CefrBadge } from '@/components/CefrBadge'
import { StatusBadge } from '@/components/StatusBadge'
import { cn } from '@/components/ui/cn'
import { highlightMatch } from '@/lib/highlightMatch'
import { translationPreviewExtraCount } from '@/lib/translationPreview'
import type { UserWordSummary } from '@/types/user'

const PREVIEW_MAX = 2

function KnownTranslationsPreview({
  translations,
  searchQuery,
}: {
  translations: string[]
  searchQuery: string
}) {
  const shown = translations.slice(0, PREVIEW_MAX)
  const extra = translationPreviewExtraCount(translations, PREVIEW_MAX)

  return (
    <>
      {shown.map((t, index) => (
        <span key={`${t}-${index}`}>
          {index > 0 && ', '}
          {index === 0 ? (
            <span className="font-medium text-foreground/90">
              {highlightMatch(t, searchQuery)}
            </span>
          ) : (
            highlightMatch(t, searchQuery)
          )}
        </span>
      ))}
      {extra > 0 && <span className="text-muted-foreground"> +{extra}</span>}
    </>
  )
}

export const WORD_DRAG_TYPE = 'application/x-lexio-user-word'

interface WordCardProps {
  userWord: UserWordSummary
  onDelete?: (id: string) => void
  deleteAriaLabel?: string
  className?: string
  draggable?: boolean
  onAddToCollection?: (word: UserWordSummary) => void
  searchQuery?: string
  variant?: 'list' | 'grid'
  /** Brief visual cue after add / jump-to-word. */
  highlighted?: boolean
}

export function WordCard({
  userWord,
  onDelete,
  deleteAriaLabel = 'Delete word',
  className,
  draggable = false,
  onAddToCollection,
  searchQuery,
  variant = 'list',
  highlighted = false,
}: WordCardProps) {
  const navigate = useNavigate()
  const isGrid = variant === 'grid'
  const isPhrase = userWord.unitKind === 'PHRASE'
  const openDetail = () => {
    if (isPhrase) return
    navigate(`/vocabulary/${userWord.id}`)
  }

  return (
    <div
      className={cn(
        'group scroll-mt-14 rounded-lg border bg-card shadow-sm transition-all hover:border-primary/30 hover:bg-accent/50 hover:shadow-md',
        isPhrase ? 'cursor-default' : 'cursor-pointer',
        isGrid ? 'flex flex-col gap-2 p-4' : 'flex items-center gap-3 px-3 py-3',
        highlighted && 'border-primary/50 bg-primary/10 ring-2 ring-primary/40',
        className,
      )}
      onClick={openDetail}
      data-testid={`word-card-${userWord.word}`}
      data-user-word-id={userWord.id}
      role={isPhrase ? undefined : 'button'}
      tabIndex={isPhrase ? undefined : 0}
      onKeyDown={(e) => e.key === 'Enter' && openDetail()}
      draggable={draggable && !isPhrase}
      onDragStart={(e) => {
        if (!draggable) return
        e.dataTransfer.setData(WORD_DRAG_TYPE, userWord.id)
        e.dataTransfer.effectAllowed = 'copy'
      }}
    >
      <div className={cn('flex min-w-0 flex-1 items-start gap-2', isGrid && 'w-full')}>
        {draggable && !isGrid && (
          <GripVertical
            className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-muted-foreground/50 active:cursor-grabbing"
            aria-hidden
          />
        )}
        {onAddToCollection && !isGrid && !isPhrase && (
          <button
            type="button"
            className="mt-0.5 shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={`Add ${userWord.word} to collection`}
            onClick={(e) => {
              e.stopPropagation()
              onAddToCollection(userWord)
            }}
          >
            <FolderPlus className="h-4 w-4" />
          </button>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('truncate font-medium text-foreground', isGrid && 'text-base')}>
              {highlightMatch(userWord.word, searchQuery ?? '')}
            </span>
            {isPhrase && userWord.phraseUnitType && (
              <span className="shrink-0 rounded border border-amber-500/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-400">
                {userWord.phraseUnitType.replaceAll('_', ' ').toLowerCase()}
              </span>
            )}
            {userWord.partOfSpeech && !isPhrase && (
              <span className="shrink-0 text-xs italic text-muted-foreground">
                {userWord.partOfSpeech}
              </span>
            )}
          </div>
          <p className={cn('mt-0.5 truncate text-sm text-muted-foreground', isGrid && 'text-xs')}>
            {userWord.knownTranslations && userWord.knownTranslations.length > 1 ? (
              <KnownTranslationsPreview
                translations={userWord.knownTranslations}
                searchQuery={searchQuery ?? ''}
              />
            ) : (
              highlightMatch(userWord.translation, searchQuery ?? '')
            )}
          </p>
        </div>

        {!isGrid && onDelete && (
          <button
            className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(userWord.id)
            }}
            aria-label={deleteAriaLabel}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className={cn('flex shrink-0 items-center gap-2', isGrid && 'w-full justify-between')}>
        <div className="flex items-center gap-2">
            {userWord.cefrLevel && <CefrBadge level={userWord.cefrLevel} />}
            <StatusBadge status={userWord.status} />
        </div>
        {isGrid && draggable && (
          <GripVertical className="h-4 w-4 text-muted-foreground/40" aria-hidden />
        )}
        {isGrid && onDelete && (
          <button
            className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(userWord.id)
            }}
            aria-label={deleteAriaLabel}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}
